<template>
    <div>
        <div class="row">
            <div class="col-md-6">
                <b-form-group label="Ölçüm Yapan Firma" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.firmId`" :watch="localForm.firmId">
                        <v-select-list
                            v-model="localForm.firmId"
                            :options="firmOptions"
                            label="Name"
                            :reduce="(item) => item.Id"
                            placeholder="Firma adı yazarak arayınız..."
                            :disabled="disabled"
                            :loading="firmsLoading"
                            v-on:search="searchFirms"
                        />
                    </v-validator>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="Fiş No" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.documentNumber`" :watch="localForm.documentNumber">
                        <b-form-input v-model="localForm.documentNumber" :disabled="disabled" />
                    </v-validator>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="Tartım-1 (Dolu, kg)" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.weightValue1`" :watch="localForm.weightValue1">
                        <VCurrencyInput :value="localForm.weightValue1" @update-value="(newValue) => (localForm.weightValue1 = newValue)" :disabled="disabled" />
                    </v-validator>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="Tartım-2 (Boş, kg)" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.weightValue2`" :watch="localForm.weightValue2">
                        <VCurrencyInput :value="localForm.weightValue2" @update-value="(newValue) => (localForm.weightValue2 = newValue)" :disabled="disabled" />
                    </v-validator>
                </b-form-group>
            </div>
            <div v-if="showWeightWarning" class="col-md-12">
                <b-alert show variant="danger" class="p-1 text-center">
                    <feather-icon icon="AlertTriangleIcon" width="18" class="mr-50" />
                    <strong>Uyarı</strong>: Boş ağırlık (Tartım-2), dolu ağırlıktan (Tartım-1) büyük olamaz.
                </b-alert>
            </div>
            <div class="col-md-6">
                <b-form-group label="Giriş Tarih/Saat" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.startDate`" :watch="localForm.startDate">
                        <v-date-picker
                            :value="localForm.startDate"
                            @input="(newValue) => updateValue('startDate', newValue)"
                            type="datetime"
                            for-back
                            placeholder="Tarih ve saat seçin"
                            :disabled="disabled"
                            :disabled-date="disableEntryDates"
                            :disabled-time="disableEntryTimes"
                            style="width: 100%"
                        />
                    </v-validator>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="Çıkış Tarih/Saat" class="is-required">
                    <v-validator :errors="errors" :parameter="`${errorPrefix}.endDate`" :watch="localForm.endDate">
                        <v-date-picker
                            :value="localForm.endDate"
                            @input="(newValue) => updateValue('endDate', newValue)"
                            type="datetime"
                            for-back
                            placeholder="Tarih ve saat seçin"
                            :disabled="disabled || !localForm.startDate"
                            :disabled-date="disableExitDates"
                            :disabled-time="disableExitTimes"
                            style="width: 100%"
                        />
                    </v-validator>
                </b-form-group>
            </div>
            <div class="col-md-6 mt-1">
                <div class="summary-box">
                    <span>Net Ağırlık</span>
                    <strong>{{ netWeight }} kg</strong>
                </div>
            </div>
            <div class="col-md-6 mt-1">
                <div class="summary-box">
                    <span>Net Hacim</span>
                    <strong>{{ netVolume }} L</strong>
                </div>
            </div>

            <div class="col-md-12 mt-1">
                <b-form-group label="Kantar Fişi / Dosyası">
                    <v-file-uploader :value="localForm.file" @change="(file) => updateValue('file', file)" :accept="['png', 'jpg', 'jpeg', 'pdf']" :disabled="disabled" />
                </b-form-group>
            </div>

            <div v-if="showDensityWarning" class="col-md-12 mt-1">
                <b-alert show variant="warning" class="p-1 text-center"> <feather-icon icon="InfoIcon" width="18" class="mr-50" />Net Hacim hesaplaması için 'Yoğunluk' değeri girilmelidir. </b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import { BAlert, BFormGroup, BFormInput } from "bootstrap-vue";
import FirmOperationsApi from "@/api/firm-operations";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import VFileUploader from "@/components/v-file-uploader/VFileUploader.vue";

export default {
    name: "WeighingCard",
    components: {
        BFormGroup,
        BFormInput,
        VCurrencyInput,
        VFileUploader,
        BAlert,
    },
    props: {
        value: { type: Object, required: true },
        disabled: { type: Boolean, default: false },
        density: { type: Number, default: 0 },
        initialFirmName: { type: String, default: null },
        orderDate: { type: String, default: null },
        errors: {
            type: Object,
            default: () => ({}),
        },
        errorPrefix: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            firms: [],
            internalFirms: [],
            firmsLoading: false,
        };
    },
    computed: {
        localForm: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit("input", newValue);
            },
        },
        showWeightWarning() {
            const weight1 = this.localForm.weightValue1 || 0;
            const weight2 = this.localForm.weightValue2 || 0;
            return weight1 > 0 && weight2 > 0 && weight2 > weight1;
        },
        rawNetWeight() {
            const weight1 = Number(this.localForm.weightValue1) || 0;
            const weight2 = Number(this.localForm.weightValue2) || 0;
            return weight1 - weight2;
        },
        netWeight() {
            return this.rawNetWeight.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        rawNetVolume() {
            if (!this.density || this.density === 0) return 0;
            return this.rawNetWeight / this.density;
        },
        netVolume() {
            return this.rawNetVolume.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        showDensityWarning() {
            const hasWeights = this.rawNetWeight > 0;
            const hasDensity = this.density > 0;
            return hasWeights && !hasDensity;
        },
        firmOptions() {
            const selectedId = this.value.firmId;
            if (selectedId && !this.internalFirms.some((f) => f.Id === selectedId)) {
                const initialOption = { Id: selectedId, Name: this.initialFirmName || `${selectedId.substring(0, 8)}...` };
                return [initialOption, ...this.internalFirms];
            }
            return this.internalFirms;
        },
    },
    methods: {
        searchFirms(search, loading) {
            if (search.length < 2) return;
            loading(true);
            this.firmsLoading = true;
            FirmOperationsApi.List(search)
                .then((res) => {
                    if (res.data.Success) {
                        this.internalFirms = res.data.Data || [];
                    }
                })
                .finally(() => {
                    loading(false);
                    this.firmsLoading = false;
                });
        },
        // === GİRİŞ TARİHİ İÇİN KONTROLLER ===
        disableEntryDates(date) {
            const checkDate = new Date(date);
            checkDate.setHours(0, 0, 0, 0);

            if (this.orderDate) {
                const minDate = new Date(this.orderDate);
                minDate.setHours(0, 0, 0, 0);
                if (checkDate < minDate) return true;
            }

            if (this.localForm.endDate) {
                const exitDate = new Date(this.localForm.endDate);
                exitDate.setHours(0, 0, 0, 0);
                return checkDate > exitDate;
            }
            return false;
        },
        disableEntryTimes(date) {
            if (this.localForm.endDate) {
                const exitDate = new Date(this.localForm.endDate);
                if (date.toDateString() === exitDate.toDateString()) {
                    return date.getTime() > exitDate.getTime();
                }
            }
            return false;
        },
        disableExitDates(date) {
            const checkDate = new Date(date);
            checkDate.setHours(0, 0, 0, 0);

            if (this.orderDate) {
                const minDate = new Date(this.orderDate);
                minDate.setHours(0, 0, 0, 0);
                if (checkDate < minDate) return true;
            }

            if (this.localForm.startDate) {
                const entryDate = new Date(this.localForm.startDate);
                entryDate.setHours(0, 0, 0, 0);
                return checkDate < entryDate;
            }
            return false;
        },
        disableExitTimes(date) {
            if (this.localForm.startDate) {
                const entryDate = new Date(this.localForm.startDate);
                if (date.toDateString() === entryDate.toDateString()) {
                    return date.getTime() < entryDate.getTime();
                }
            }
            return false;
        },
        updateValue(key, value) {
            const newFormData = { ...this.localForm, [key]: value };
            this.$emit("input", newFormData);
        },
    },
};
</script>
<style scoped>
.summary-box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0.35rem;
    min-height: 60px;
    background-color: #0275d8 !important;
    border-radius: 0.35rem;
    border: 1px solid #043e7b;
}
.summary-box span {
    font-size: 1rem;
    font-weight: 700;
    color: #ffffff;
}
.summary-box strong {
    font-size: 0.9rem;
    color: #ffffff;
}
.is-required >>> .col-form-label::after,
.is-required /deep/ .col-form-label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}
</style>
